'use client';

import type { MouseEvent } from 'react';
import { useDateFormatter } from '@terra/ui-formatters';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BadgeSeverity,
  isBadgeSeverityType,
} from '../../components/styled/badge-severity';
import {
  BadgeStatus,
  FINDING_STATUS_BADGE_STATUS_MAP,
  isMappedFindingStatus,
} from '../../components/styled/badge-status';
import { BodyStrong, MetaDefault } from '../../components/typography';
import { formatFindingStatusForDisplay } from '../../utils/category-utils';
import { EntityIcon } from '../icons/EntityIcon';
import { ArchivedLifecycleBadge } from '../../components/archived-lifecycle-badge';

export type LinkedFindingRowData = {
  id: string;
  title: string | null;
  status: string | null;
  severity?: string | null;
  createdAt: string | Date;
  discoveryDate?: string | Date | null;
  isArchived?: boolean;
};

type LinkedFindingRowProps = {
  finding: LinkedFindingRowData;
  contentPaddingY?: number;
  embeddedInSurface?: boolean;
  onNavigate?: (id: string) => void;
  onContextMenu?: (event: MouseEvent<HTMLElement>, id: string) => void;
};

export const LinkedFindingRow = ({
  finding,
  contentPaddingY = 2,
  embeddedInSurface = false,
  onNavigate,
  onContextMenu,
}: LinkedFindingRowProps) => {
  const dateFormatter = useDateFormatter();
  const severity = isBadgeSeverityType(finding.severity)
    ? finding.severity
    : null;
  const findingStatus = isMappedFindingStatus(finding.status)
    ? finding.status
    : null;
  const statusBadge = findingStatus
    ? FINDING_STATUS_BADGE_STATUS_MAP[findingStatus]
    : null;
  const displayDate = finding.discoveryDate ?? finding.createdAt;
  const formattedDate = (() => {
    if (!displayDate) {
      return '-';
    }

    const date =
      displayDate instanceof Date ? displayDate : new Date(displayDate);

    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return dateFormatter.formatDateOnly(date);
  })();

  const content = (
    <VStack alignItems="stretch" gap={2}>
      <HStack gap={2} alignItems="center" justifyContent="space-between">
        <HStack gap={2} alignItems="center" minW={0} flex="1">
          <EntityIcon
            type="finding"
            appearance="highlighted"
            density="compact"
          />
          <BodyStrong truncate>{finding.title || 'Finding'}</BodyStrong>
          {finding.isArchived ? (
            <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
          ) : null}
        </HStack>
        {severity && (
          <BadgeSeverity severity={severity} size="sm">
            {severity}
          </BadgeSeverity>
        )}
      </HStack>
      <HStack gap={2} alignItems="center" justifyContent="space-between">
        {statusBadge ? (
          <BadgeStatus status={statusBadge} size="sm">
            {formatFindingStatusForDisplay(findingStatus ?? '')}
          </BadgeStatus>
        ) : finding.status ? (
          <MetaDefault color="fg.muted">{finding.status}</MetaDefault>
        ) : (
          <Box />
        )}
        <MetaDefault color="fg.muted">{formattedDate}</MetaDefault>
      </HStack>
    </VStack>
  );

  if (embeddedInSurface) {
    return content;
  }

  return (
    <Box
      py={contentPaddingY}
      px={0}
      cursor={onNavigate ? 'pointer' : 'default'}
      onClick={() => onNavigate?.(finding.id)}
      onContextMenu={(event) => {
        event.stopPropagation();
        onContextMenu?.(event, finding.id);
      }}
      _hover={onNavigate ? { bg: 'bg.hover' } : undefined}
      borderRadius="md"
    >
      {content}
    </Box>
  );
};
